import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";

import { formatDate, getAvatarUrl, getRealFileName, getUploadUrl } from "src/Common/common";
import { can, downloadImage, downloadPdf, handleError } from "src/Common/function";
import WOService from "src/Services/API/wo.service";
import ConfirmDialog from "src/Common/confirmdialog";
import AttachmentImageSlider from "src/Pages/WO/Components/attachment/AttachmentImageSlider";
import UploadDocumentsModal from "src/Pages/WO/Components/upload/UploadDocumentsModal";

// Technical documents are WoAttachment rows with type === 0 (single-table
// inheritance shares the same table with jobsite photos/forms).
const TECHNICAL_DOCUMENT_TYPE = 0;
// The /attach upload flow uses type 1 for "attachments to the work order";
// the per-file metadata.attachmentType then narrows it to a technical document.
const ATTACHMENT_UPLOAD_FLOW = 1;
const PREVIEW_LIMIT = 5;

// Map documentType -> badge colors. Keys match the document-type options in the
// upload modal (see DOCUMENT_TYPE_OPTIONS). Falls back to a neutral style for
// any value we don't have an explicit color for.
const TYPE_BADGE_STYLES = {
  Drawing: "bg-[#EAF3FF] text-[#0058E5]",
  Manual: "bg-[#FEF9C3] text-[#A16207]",
  "Spec Sheet": "bg-[#DCFCE7] text-[#15803D]",
  Warranty: "bg-[#F3E8FF] text-[#7E22CE]",
};
const DEFAULT_BADGE_STYLE = "bg-[#EEF0F9] text-[#5F6477]";

const isPdfFile = (fileName) => !!fileName && fileName.toLowerCase().endsWith(".pdf");

const getFileExtension = (fileName) => {
  if (!fileName) return "";
  const lastDot = fileName.lastIndexOf(".");
  return lastDot >= 0 ? fileName.substring(lastDot + 1).toUpperCase() : "";
};

// Build the name used when downloading. Prefer the user-facing documentName,
// falling back to the real stored file name. The original file's extension is
// appended when documentName doesn't already carry one.
const getDownloadFileName = (document) => {
  const realFileName = getRealFileName(document.fileName) || document.fileName;
  const baseName = document.documentName?.trim();
  if (!baseName) return realFileName;
  const lastDot = realFileName.lastIndexOf(".");
  const extension = lastDot >= 0 ? realFileName.substring(lastDot) : "";
  return extension && !baseName.toLowerCase().endsWith(extension.toLowerCase()) ? `${baseName}${extension}` : baseName;
};

const ChevronIcon = ({ expanded }) => (
  <svg
    className={`w-4 h-4 text-[#7C829A] transition-transform duration-200 ${expanded ? "" : "rotate-180"}`}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5 12.5L10 7.5L15 12.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DownloadMenuIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6 8.5V0.5C6 0.223858 6.22386 0 6.5 0C6.77614 0 7 0.223858 7 0.5V8.5C7 8.77614 6.77614 9 6.5 9C6.22386 9 6 8.77614 6 8.5Z"
      fill="#7C829A"
    />
    <path
      d="M0 11.1667V8.5C0 8.22386 0.223858 8 0.5 8C0.776143 8 1 8.22386 1 8.5V11.1667C1 11.3877 1.08786 11.5996 1.24414 11.7559C1.40042 11.9121 1.61232 12 1.83333 12H11.1667C11.3877 12 11.5996 11.9121 11.7559 11.7559C11.9121 11.5996 12 11.3877 12 11.1667V8.5C12 8.22386 12.2239 8 12.5 8C12.7761 8 13 8.22386 13 8.5V11.1667C13 11.6529 12.8067 12.1191 12.4629 12.4629C12.1191 12.8067 11.6529 13 11.1667 13H1.83333C1.3471 13 0.880926 12.8067 0.537109 12.4629C0.193293 12.1191 0 11.6529 0 11.1667Z"
      fill="#7C829A"
    />
    <path
      d="M9.47982 4.81315C9.67508 4.61789 9.99159 4.61789 10.1869 4.81315C10.3821 5.00841 10.3821 5.32492 10.1869 5.52018L6.85352 8.85352C6.65825 9.04878 6.34175 9.04878 6.14649 8.85352L2.81315 5.52018C2.61789 5.32492 2.61789 5.00841 2.81315 4.81315C3.00841 4.61789 3.32492 4.61789 3.52018 4.81315L6.5 7.79297L9.47982 4.81315Z"
      fill="#7C829A"
    />
  </svg>
);

const DeleteMenuIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14 3.49992C14.2761 3.49992 14.5 3.72378 14.5 3.99992C14.5 4.27606 14.2761 4.49992 14 4.49992H2C1.72386 4.49992 1.5 4.27606 1.5 3.99992C1.5 3.72378 1.72386 3.49992 2 3.49992H14Z"
      fill="#EF4444"
    />
    <path
      d="M2.83333 13.3333V3.99992C2.83333 3.72378 3.05719 3.49992 3.33333 3.49992C3.60948 3.49992 3.83333 3.72378 3.83333 3.99992V13.3333C3.83333 13.504 3.92369 13.7166 4.10352 13.8964C4.28334 14.0762 4.49595 14.1666 4.66667 14.1666H11.3333C11.5041 14.1666 11.7167 14.0762 11.8965 13.8964C12.0763 13.7166 12.1667 13.504 12.1667 13.3333V3.99992C12.1667 3.72378 12.3905 3.49992 12.6667 3.49992C12.9428 3.49992 13.1667 3.72378 13.1667 3.99992V13.3333C13.1667 13.8292 12.9237 14.2833 12.6035 14.6034C12.2833 14.9236 11.8293 15.1666 11.3333 15.1666H4.66667C4.17072 15.1666 3.71666 14.9236 3.39648 14.6034C3.07631 14.2833 2.83333 13.8292 2.83333 13.3333Z"
      fill="#EF4444"
    />
    <path
      d="M10.1667 3.99992V2.66659C10.1667 2.49587 10.0763 2.28326 9.89649 2.10343C9.71666 1.92361 9.50405 1.83325 9.33333 1.83325H6.66667C6.49595 1.83325 6.28334 1.92361 6.10352 2.10343C5.92369 2.28326 5.83333 2.49587 5.83333 2.66659V3.99992C5.83333 4.27606 5.60948 4.49992 5.33333 4.49992C5.05719 4.49992 4.83333 4.27606 4.83333 3.99992V2.66659C4.83333 2.17064 5.07631 1.71658 5.39649 1.3964C5.71666 1.07623 6.17072 0.833252 6.66667 0.833252H9.33333C9.82928 0.833252 10.2833 1.07623 10.6035 1.3964C10.9237 1.71658 11.1667 2.17064 11.1667 2.66659V3.99992C11.1667 4.27606 10.9428 4.49992 10.6667 4.49992C10.3905 4.49992 10.1667 4.27606 10.1667 3.99992Z"
      fill="#EF4444"
    />
    <path
      d="M6.16667 11.3333V7.33325C6.16667 7.05711 6.39053 6.83325 6.66667 6.83325C6.94281 6.83325 7.16667 7.05711 7.16667 7.33325V11.3333C7.16667 11.6094 6.94281 11.8333 6.66667 11.8333C6.39053 11.8333 6.16667 11.6094 6.16667 11.3333Z"
      fill="#EF4444"
    />
    <path
      d="M8.83333 11.3333V7.33325C8.83333 7.05711 9.05719 6.83325 9.33333 6.83325C9.60948 6.83325 9.83334 7.05711 9.83334 7.33325V11.3333C9.83334 11.6094 9.60948 11.8333 9.33333 11.8333C9.05719 11.8333 8.83333 11.6094 8.83333 11.3333Z"
      fill="#EF4444"
    />
  </svg>
);

const ServiceTicketToggle = ({ checked, disabled, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={onChange}
    className={`relative inline-flex h-[20px] w-[36px] shrink-0 items-center rounded-[12px] p-[2px] transition-colors ${
      checked ? "bg-[#0065FD]" : "bg-[#EEF0F9]"
    } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
  >
    <span
      className={`inline-block h-[16px] w-[16px] transform rounded-full bg-white shadow-[0px_1px_2px_rgba(10,12.67,18,0.06),0px_1px_3px_rgba(10,12.67,18,0.10)] transition-transform ${
        checked ? "translate-x-[16px]" : "translate-x-0"
      }`}
    />
  </button>
);

function TechnicalDocuments({ order, onRefresh }) {
  const [expanded, setExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  // Fixed-position coordinates for the open menu. The card has overflow-hidden,
  // so the menu is portaled to the body and positioned from the button's rect,
  // flipping above the button when there isn't room below.
  const [menuPosition, setMenuPosition] = useState(null);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [sliderIndex, setSliderIndex] = useState(null);

  const documents = useMemo(() => {
    const attachments = order?.attachments || [];
    return attachments.filter((attachment) => Number(attachment?.type) === TECHNICAL_DOCUMENT_TYPE);
  }, [order?.attachments]);

  const visibleDocuments = showAll ? documents : documents.slice(0, PREVIEW_LIMIT);

  const getUploaderName = (document) =>
    document?.uploadedBy?.name ||
    document?.uploadedByCustomerUser?.customer?.companyName ||
    document?.uploadedByCustomerUser?.account?.name ||
    "-";

  // Map documents to the shape AttachmentImageSlider expects.
  const sliderAttachments = useMemo(
    () =>
      documents.map((document) => ({
        id: document.id,
        fileName: document.fileName,
        isPdf: isPdfFile(document.fileName),
        displayName: document.documentName || getRealFileName(document.fileName) || document.fileName,
        description: document.description || "",
        tags: document.tags || [],
        createdBy: getUploaderName(document),
        createdAt: document.createdAt,
      })),
    [documents],
  );

  const handleToggleServiceTicket = async (document) => {
    if (togglingId) return;
    setTogglingId(document.id);
    try {
      const response = await WOService.updateAttachment({
        id: document.id,
        data: { includeInServiceTicket: !document.includeInServiceTicket },
      });
      if (response.status === 200 || response.status === 201) {
        onRefresh?.();
      } else {
        toast("Something went wrong");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setTogglingId(null);
    }
  };

  const handleView = (document) => {
    if (!document?.fileName) return;
    const index = sliderAttachments.findIndex((attachment) => attachment.id === document.id);
    if (index >= 0) setSliderIndex(index);
  };

  const closeMenu = () => {
    setOpenMenuId(null);
    setMenuPosition(null);
  };

  const handleToggleMenu = (event, document) => {
    if (openMenuId === document.id) {
      closeMenu();
      return;
    }
    const MENU_WIDTH = 180;
    // Approximate height: one item (~40px) plus the delete item when shown,
    // plus the container's vertical padding.
    const estimatedHeight = (can("wo:deleteTechnicalDocument") ? 80 : 40) + 8;
    const rect = event.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < estimatedHeight + 8 && rect.top > spaceBelow;
    setMenuPosition({
      top: openUp ? rect.top - estimatedHeight - 4 : rect.bottom + 4,
      // Right-align the menu to the button.
      left: Math.max(8, rect.right - MENU_WIDTH),
    });
    setOpenMenuId(document.id);
  };

  const handleDownload = (document) => {
    closeMenu();
    if (!document?.fileName) return;
    const downloadName = getDownloadFileName(document);
    if (isPdfFile(document.fileName)) {
      downloadPdf(getUploadUrl(document.fileName), downloadName);
    } else {
      downloadImage(getUploadUrl(document.fileName), downloadName);
    }
  };

  const handleDelete = async () => {
    const document = documentToDelete;
    setDocumentToDelete(null);
    if (!document?.id || !order?.id) return;
    try {
      await WOService.deleteTechnicalDocument(order.id, document.id, ATTACHMENT_UPLOAD_FLOW);
      toast("Document deleted successfully");
      onRefresh?.();
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <div className="w-full bg-white rounded-[12px] overflow-hidden shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-[#E5E8F3]">
      {/* Header (click anywhere to expand/collapse) */}
      <div
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full h-[52px] px-6 flex items-center justify-between bg-[#F8F9FE] border-b border-[#E5E8F3] cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#1A2332]">
            Technical Documents
          </span>
          <span className="min-w-[24px] px-2 py-[2px] flex items-center justify-center rounded-[16px] bg-[#EEF0F9] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#5F6477]">
            {documents.length}
          </span>
        </div>
        <div className="flex items-center gap-4">
          {can("wo:addTechnicalDocument") && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowUploadModal(true);
              }}
              className="h-[32px] px-3 rounded-[8px] bg-[#0065FD] flex items-center justify-center gap-2 font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-white cursor-pointer"
            >
              Upload
            </button>
          )}
          <ChevronIcon expanded={expanded} />
        </div>
      </div>

      {/* Grid-rows 0fr->1fr animates the body height without a fixed max-height. */}
      <div
        className={`w-full grid transition-[grid-template-rows] duration-300 ease-in-out ${
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="w-full min-h-0 overflow-hidden">
          {/* Column headers */}
          <div className="w-full grid grid-cols-[minmax(0,2fr)_1fr_1.2fr_1.4fr_0.9fr_auto] items-center gap-3 h-[44px] px-6 bg-[#FBFCFF] border-b border-[#E5E8F3]">
            <span className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#5F6477]">
              File name
            </span>
            <span className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#5F6477]">
              Type
            </span>
            <span className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#5F6477]">
              Uploaded By
            </span>
            <span className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#5F6477]">
              Uploaded Date
            </span>
            <span className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#5F6477]">
              Service Ticket
            </span>
            <span className="w-[100px]" />
          </div>

          {visibleDocuments.length === 0 ? (
            <div className="w-full px-6 py-6">
              <span className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477]">
                No technical documents
              </span>
            </div>
          ) : (
            visibleDocuments.map((document) => {
              const displayName = document.documentName || getRealFileName(document.fileName) || document.fileName;
              const extension = getFileExtension(document.fileName);
              const badgeStyle = TYPE_BADGE_STYLES[document.documentType] || DEFAULT_BADGE_STYLE;
              return (
                <div
                  key={document.id}
                  className="w-full grid grid-cols-[minmax(0,2fr)_1fr_1.2fr_1.4fr_0.9fr_auto] items-center gap-3 h-[72px] px-6 border-b border-[#E5E8F3]"
                >
                  {/* File name + size/ext */}
                  <div className="min-w-0 flex flex-col">
                    <span
                      className="font-OpenSansSemiBold text-[14px] leading-[20px] text-[#1A2332] truncate"
                      title={displayName}
                    >
                      {displayName}
                    </span>
                    {extension && (
                      <span className="font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#5F6477]">
                        {extension}
                      </span>
                    )}
                  </div>

                  {/* Type badge */}
                  <div className="min-w-0">
                    {document.documentType ? (
                      <span
                        className={`inline-block px-2 py-[2px] rounded-[6px] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize ${badgeStyle}`}
                      >
                        {document.documentType}
                      </span>
                    ) : (
                      <span className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477]">-</span>
                    )}
                  </div>

                  {/* Uploaded by */}
                  <div className="min-w-0 flex items-center gap-3">
                    {document.uploadedBy?.avatar && (
                      <img
                        src={getAvatarUrl(document.uploadedBy.avatar)}
                        alt=""
                        className="w-[24px] h-[24px] rounded-full object-cover"
                      />
                    )}
                    <span className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477] truncate">
                      {getUploaderName(document)}
                    </span>
                  </div>

                  {/* Uploaded date */}
                  <span className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477]">
                    {document.createdAt ? formatDate(document.createdAt, "MMMM D, YYYY h:mm A") : "-"}
                  </span>

                  {/* Service ticket toggle */}
                  <div>
                    <ServiceTicketToggle
                      checked={!!document.includeInServiceTicket}
                      disabled={!can("wo:updateAttachment") || togglingId === document.id}
                      onChange={() => handleToggleServiceTicket(document)}
                    />
                  </div>

                  {/* View + kebab menu */}
                  <div className="w-[100px] flex items-center justify-end gap-3 relative">
                    <button
                      type="button"
                      onClick={() => handleView(document)}
                      className="h-[32px] px-1 font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#0065FD] cursor-pointer"
                    >
                      View
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleToggleMenu(e, document)}
                      className="p-[10px] rounded-[8px] text-[#5F6477] cursor-pointer hover:bg-slate-50"
                    >
                      &#8942;
                    </button>
                    {openMenuId === document.id &&
                      menuPosition &&
                      createPortal(
                        <>
                          {/* Click-away overlay */}
                          <div className="fixed inset-0 z-[1000]" onClick={closeMenu} />
                          <div
                            className="fixed z-[1001] w-[180px] flex flex-col gap-[2px] px-1 py-1 bg-white rounded-[12px] border border-[#E5E8F3] shadow-[0px_4px_12px_0px_rgba(10,12.67,18,0.10)]"
                            style={{ top: menuPosition.top, left: menuPosition.left }}
                          >
                            <button
                              type="button"
                              onClick={() => handleDownload(document)}
                              className="w-full min-h-[36px] px-3 py-[6px] flex items-center gap-3 rounded-[8px] text-left hover:bg-[#F8F9FE]"
                            >
                              <DownloadMenuIcon />
                              <span className="flex-1 font-InterMedium text-[14px] leading-[20px] text-[#1A2332]">
                                Download
                              </span>
                            </button>
                            {can("wo:deleteTechnicalDocument") && (
                              <button
                                type="button"
                                onClick={() => {
                                  closeMenu();
                                  setDocumentToDelete(document);
                                }}
                                className="w-full min-h-[36px] px-3 py-[6px] flex items-center gap-3 rounded-[8px] text-left hover:bg-[#F8F9FE]"
                              >
                                <DeleteMenuIcon />
                                <span className="flex-1 font-InterMedium text-[14px] leading-[20px] text-[#EF4444]">
                                  Delete file
                                </span>
                              </button>
                            )}
                          </div>
                        </>,
                        window.document.body,
                      )}
                  </div>
                </div>
              );
            })
          )}

          {documents.length > PREVIEW_LIMIT && (
            <div className="w-full px-6 pt-3 pb-4 border-t border-[#E5E8F3] flex items-center justify-center">
              <button
                type="button"
                onClick={() => setShowAll((prev) => !prev)}
                className="h-[32px] px-1 font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#0065FD] cursor-pointer"
              >
                {showAll ? "Show Less" : "View All"}
              </button>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        title="DELETE DOCUMENT"
        description="Are you sure you want to delete this technical document?"
        shown={!!documentToDelete}
        onConfirmClicked={handleDelete}
        onCancelClicked={() => setDocumentToDelete(null)}
      />

      <AttachmentImageSlider
        shown={sliderIndex !== null}
        attachments={sliderAttachments}
        activeIndex={sliderIndex ?? 0}
        onCancel={() => setSliderIndex(null)}
      />

      <UploadDocumentsModal
        shown={showUploadModal}
        order={order}
        onClose={() => setShowUploadModal(false)}
        onUploaded={onRefresh}
      />
    </div>
  );
}

export default TechnicalDocuments;
